import type { Category, Expense, PullPage, ReceiptMeta } from './types';

/**
 * Pure sync state so it can be unit-tested. Same rules as the mobile sync core:
 * the server copy wins unless this client holds a newer unpushed edit.
 */
export type DataState = {
  categories: Map<string, Category>;
  expenses: Map<string, Expense>;
  /** Live (non-deleted) receipt per expense. */
  receipts: Map<string, ReceiptMeta>;
  pendingCategories: Map<string, Category>;
  pendingExpenses: Map<string, Expense>;
  cursor: number;
};

export function emptyState(): DataState {
  return {
    categories: new Map(),
    expenses: new Map(),
    receipts: new Map(),
    pendingCategories: new Map(),
    pendingExpenses: new Map(),
    cursor: 0,
  };
}

export function applyPull(state: DataState, page: PullPage): DataState {
  const next: DataState = {
    ...state,
    categories: new Map(state.categories),
    expenses: new Map(state.expenses),
    receipts: new Map(state.receipts),
    cursor: page.cursor,
  };
  for (const { version: _v, ...c } of page.categories) {
    const pending = state.pendingCategories.get(c.id);
    if (pending && pending.updatedAt > c.updatedAt) continue;
    next.categories.set(c.id, c);
  }
  for (const { version: _v, ...e } of page.expenses) {
    const pending = state.pendingExpenses.get(e.id);
    if (pending && pending.updatedAt > e.updatedAt) continue;
    next.expenses.set(e.id, e);
  }
  for (const r of page.receipts) {
    const current = next.receipts.get(r.expenseId);
    if (r.deletedAt) {
      if (current?.id === r.id) next.receipts.delete(r.expenseId);
    } else if (!current || current.version <= r.version) {
      next.receipts.set(r.expenseId, r);
    }
  }
  return next;
}

/** A local edit: visible immediately, queued for the next push. */
export function stageExpense(state: DataState, e: Expense): DataState {
  return {
    ...state,
    expenses: new Map(state.expenses).set(e.id, e),
    pendingExpenses: new Map(state.pendingExpenses).set(e.id, e),
  };
}

export function stageCategory(state: DataState, c: Category): DataState {
  return {
    ...state,
    categories: new Map(state.categories).set(c.id, c),
    pendingCategories: new Map(state.pendingCategories).set(c.id, c),
  };
}

/** After a successful push, drop pending entries that haven't changed since they were sent. */
export function markPushed(state: DataState, sent: { categories: Category[]; expenses: Expense[] }): DataState {
  const pc = new Map(state.pendingCategories);
  const pe = new Map(state.pendingExpenses);
  for (const c of sent.categories) if (pc.get(c.id)?.updatedAt === c.updatedAt) pc.delete(c.id);
  for (const e of sent.expenses) if (pe.get(e.id)?.updatedAt === e.updatedAt) pe.delete(e.id);
  return { ...state, pendingCategories: pc, pendingExpenses: pe };
}

export const liveExpenses = (s: DataState) => [...s.expenses.values()].filter((e) => !e.deletedAt);

export const liveCategories = (s: DataState) =>
  [...s.categories.values()]
    .filter((c) => !c.deletedAt)
    .sort((a, b) => Number(a.isArchived) - Number(b.isArchived) || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
